import { ChevronUp, SquarePen } from 'lucide-react';
import { useLayoutEffect, useRef, useState } from 'react';
import type { JSX, KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';

import { UserAvatar } from '@/components/user-avatar';
import { cn } from '@/lib/utils';

import { CHAT_MAX_RUNES } from '../realtime/frames';
import type { ChatMessage, SystemChatKind } from '../realtime/frames';

/** Props of the live chat panel: the loaded rows plus every action and notice flag. */
export interface ChatPanelProps {
  readonly messages: readonly ChatMessage[];
  /** Own identity: messages authored by it render right-aligned with the emerald accent. */
  readonly ownIdentity: string | null;
  /** True while the realtime channel is not live: the composer disables. */
  readonly disabled: boolean;
  readonly hasMore: boolean;
  readonly loadingOlder: boolean;
  readonly rateLimited: boolean;
  readonly storageError: boolean;
  readonly onLoadOlder: () => void;
  /** Sends one message; returns false when the body violates the contract. */
  readonly onSend: (body: string) => boolean;
}

/** Show the character counter from this length on (90% of the cap). */
const COUNTER_THRESHOLD = CHAT_MAX_RUNES * 0.9;
/** Distance (px) from the bottom within which the view still counts as stuck to it. */
const STICK_BOTTOM_SLACK_PX = 48;

/**
 * The «Чат» tab in the Syncra look: the persisted history (older pages prepend on demand),
 * live rows as a messenger log (avatar, name — emerald «Вы» for own rows —, time and text),
 * and a composer bound to the 2000-rune contract with the white send square. System rows
 * render centered and muted. New messages autoscroll only while the user has not scrolled up.
 */
export function ChatPanel(props: ChatPanelProps): JSX.Element {
  const { t } = useTranslation();
  const [draft, setDraft] = useState('');
  const listRef = useRef<HTMLDivElement | null>(null);
  const stickToBottomRef = useRef(true);
  const pendingPrependHeightRef = useRef<number | null>(null);
  const firstIdRef = useRef<number | null>(props.messages[0]?.id ?? null);

  const scrollToBottom = (): void => {
    const element = listRef.current;
    if (element !== null) {
      element.scrollTop = element.scrollHeight;
    }
  };

  // Keep the viewport pinned to the newest message while the user has not scrolled up, and
  // anchor it to the same rows after an older page has been prepended.
  useLayoutEffect(() => {
    const element = listRef.current;
    if (element === null) {
      return;
    }
    const firstId = props.messages[0]?.id ?? null;
    const hadPrepend =
      pendingPrependHeightRef.current !== null &&
      firstId !== null &&
      firstId !== firstIdRef.current;
    if (hadPrepend) {
      const previousHeight = pendingPrependHeightRef.current ?? 0;
      element.scrollTop = element.scrollHeight - previousHeight;
      pendingPrependHeightRef.current = null;
    } else if (stickToBottomRef.current) {
      scrollToBottom();
    }
    firstIdRef.current = firstId;
  }, [props.messages]);

  function handleScroll(): void {
    const element = listRef.current;
    if (element === null) {
      return;
    }
    stickToBottomRef.current =
      element.scrollHeight - element.scrollTop - element.clientHeight <= STICK_BOTTOM_SLACK_PX;
  }

  function handleLoadOlder(): void {
    const element = listRef.current;
    if (element !== null) {
      pendingPrependHeightRef.current = element.scrollHeight;
    }
    props.onLoadOlder();
  }

  function sendDraft(): void {
    if (props.disabled || draft.trim().length === 0) {
      return;
    }
    if (props.onSend(draft)) {
      setDraft('');
      stickToBottomRef.current = true;
    }
  }

  function handleKeyDown(event: KeyboardEvent<HTMLTextAreaElement>): void {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      sendDraft();
    }
  }

  return (
    <div data-testid="chat-panel" className="flex h-full min-h-0 flex-col">
      <div
        ref={listRef}
        onScroll={handleScroll}
        className="min-h-0 flex-1 space-y-3.5 overflow-y-auto p-3 scroll-thin-dark"
        aria-label={t('chat.messages_aria')}
      >
        {props.hasMore ? (
          <div className="flex justify-center pb-2">
            <button
              type="button"
              data-testid="chat-load-older"
              disabled={props.loadingOlder}
              onClick={handleLoadOlder}
              className="flex items-center gap-1 rounded-full border border-white/10 bg-zinc-800/60 px-3 py-1 text-[11px] font-medium text-zinc-300 transition-colors hover:bg-zinc-700 hover:text-white disabled:cursor-not-allowed disabled:opacity-50"
            >
              <ChevronUp className="h-3 w-3" />
              {props.loadingOlder ? t('chat.loading_older') : t('chat.load_older')}
            </button>
          </div>
        ) : null}
        {props.messages.length === 0 ? (
          <p className="px-2 py-6 text-center text-xs text-zinc-500">{t('chat.empty')}</p>
        ) : null}
        <ul className="flex flex-col gap-3.5">
          {props.messages.map((message) => (
            <ChatRow
              key={message.id}
              message={message}
              own={message.identity === props.ownIdentity}
            />
          ))}
        </ul>
      </div>
      <div className="shrink-0 border-t border-white/[0.06] p-3">
        {props.rateLimited ? (
          <p
            role="status"
            data-testid="chat-rate-limited"
            className="px-1 pb-1.5 text-[11px] text-amber-300"
          >
            {t('chat.rate_limited')}
          </p>
        ) : null}
        {props.storageError ? (
          <p
            role="status"
            data-testid="chat-storage-error"
            className="px-1 pb-1.5 text-[11px] text-amber-300"
          >
            {t('chat.storage_error')}
          </p>
        ) : null}
        <div className="flex items-center gap-2">
          <textarea
            data-testid="chat-input"
            aria-label={t('chat.composer_aria')}
            placeholder={
              props.disabled ? t('chat.composer_reconnecting') : t('chat.composer_placeholder')
            }
            disabled={props.disabled}
            value={draft}
            maxLength={CHAT_MAX_RUNES}
            rows={1}
            onChange={(event) => {
              setDraft(event.target.value);
            }}
            onKeyDown={handleKeyDown}
            className="max-h-28 min-h-9 flex-1 resize-none rounded-lg border border-white/10 bg-white/[0.05] px-3 py-2 text-[12.5px] text-white placeholder:text-zinc-600 focus:border-white/20 focus:bg-white/[0.07] focus:outline-none disabled:cursor-not-allowed disabled:opacity-50"
          />
          <button
            type="button"
            data-testid="chat-send"
            aria-label={t('chat.send_aria')}
            disabled={props.disabled || draft.trim().length === 0}
            onClick={sendDraft}
            className="grid size-9 shrink-0 place-items-center rounded-lg bg-white text-zinc-950 transition-colors hover:bg-zinc-200 disabled:cursor-not-allowed disabled:bg-zinc-800 disabled:text-zinc-600"
          >
            <SquarePen className="size-3.5" />
          </button>
        </div>
        {draft.length >= COUNTER_THRESHOLD ? (
          <p data-testid="chat-counter" className="px-1 pt-1 text-right text-[10px] text-zinc-500">
            {draft.length}/{CHAT_MAX_RUNES}
          </p>
        ) : null}
      </div>
    </div>
  );
}

/**
 * The i18next key of one system row kind. Exhaustive over the closed vocabulary the server
 * ships (frames.ts SYSTEM_CHAT_KINDS); the keys exist in both en and ru catalogs.
 */
type SystemChatKey = `chat.system_${SystemChatKind}`;

function systemChatKey(kind: SystemChatKind): SystemChatKey {
  switch (kind) {
    case 'joined':
      return 'chat.system_joined';
    case 'left':
      return 'chat.system_left';
    case 'muted':
      return 'chat.system_muted';
    case 'mute_all':
      return 'chat.system_mute_all';
    case 'kicked':
      return 'chat.system_kicked';
    case 'asked_stop_share':
      return 'chat.system_asked_stop_share';
    case 'promoted':
      return 'chat.system_promoted';
    case 'demoted':
      return 'chat.system_demoted';
    case 'recording_started':
      return 'chat.system_recording_started';
    case 'recording_stopped':
      return 'chat.system_recording_stopped';
  }
}

/**
 * Renders one system row in the UI language when it carries a structured kind (interpolating
 * its display-name params), and falls back to the canonical English body otherwise - rows
 * persisted before the 0007 migration have no kind.
 */
function systemChatText(message: ChatMessage, t: TFunction): string {
  if (message.kind === undefined) {
    return message.body;
  }
  return t(systemChatKey(message.kind), { ...message.params });
}

interface ChatRowProps {
  readonly message: ChatMessage;
  readonly own: boolean;
}

/**
 * One chat row in the Syncra look: a messenger-log line with the avatar, the name (emerald
 * «Вы» for own rows), the time and the text below. System rows stay centered and muted.
 */
function ChatRow({ message, own }: ChatRowProps): JSX.Element {
  const { t } = useTranslation();
  if (message.system) {
    return (
      <li
        data-testid={`chat-message-${message.id}`}
        data-message-kind="system"
        className="px-4 text-center text-[11px] italic text-zinc-500"
      >
        <span data-testid="chat-message">{systemChatText(message, t)}</span>
      </li>
    );
  }
  const time = formatMessageTime(message.ts);
  return (
    <li data-testid={`chat-message-${message.id}`} className="flex gap-2.5">
      <UserAvatar
        name={message.displayName}
        size="sm"
        className="mt-0.5 size-5 shrink-0 text-[9px]"
      />
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline gap-2">
          <span
            className={cn('text-[12px] font-semibold', own ? 'text-emerald-400' : 'text-zinc-200')}
          >
            {own ? t('participants.you_display') : message.displayName}
          </span>
          {time.length > 0 ? (
            <span className="text-[10.5px] tabular-nums text-zinc-500">{time}</span>
          ) : null}
        </div>
        <p
          data-testid="chat-message"
          className="mt-0.5 text-[12.5px] leading-relaxed break-words whitespace-pre-wrap text-zinc-400"
        >
          {message.body}
        </p>
      </div>
    </li>
  );
}

/** Formats an RFC 3339 timestamp as local HH:MM; empty string when unparsable. */
export function formatMessageTime(ts: string): string {
  const date = new Date(ts);
  if (Number.isNaN(date.getTime())) {
    return '';
  }
  const hours = date.getHours().toString().padStart(2, '0');
  const minutes = date.getMinutes().toString().padStart(2, '0');
  return `${hours}:${minutes}`;
}
